<template>
  <page-layout
      :title="pageTitle"
      :breadcrumb="pageBreadcrumb"
      :status="pageStatus"
      :code="pageCode"
      :info-cards="pageInfoCards"
  >
    <template #actions>
      <md-button
          v-if="payment.current_status === 'draft'"
          class="md-primary md-round"
          @click="goToEdit(payment.id)"
      >
        <md-icon>edit</md-icon>
        Edit
      </md-button>

      <md-button
          v-if="payment.current_status === 'draft'"
          class="md-success md-round"
          @click="approvePayment"
      >
        <md-icon>check_circle</md-icon>
        Approve
      </md-button>

      <md-button
          v-if="payment.current_status === 'approved'"
          class="md-info md-round"
          @click="markAsPaid"
      >
        <md-icon>payment</md-icon>
        Mark as Paid
      </md-button>

      <md-button
          v-if="payment.current_status !== 'paid' && payment.current_status !== 'cancelled'"
          class="md-danger md-round"
          @click="cancelPayment"
      >
        <md-icon>cancel</md-icon>
        Cancel
      </md-button>

      <md-button class="md-info md-round" @click="goBack">
        <md-icon>arrow_back</md-icon>
        Back
      </md-button>
    </template>

    <template #content>
      <page-tabs>
        <!-- Summary Tab -->
        <md-tab id="tab-summary" md-label="Summary" md-icon="receipt_long">
          <div class="tab-content">

            <!-- Details Grid -->
            <div class="details-grid">
              <div class="detail-card">
                <div class="detail-card-header">
                  <md-icon>info</md-icon>
                  <h3>Payment Information</h3>
                </div>
                <div class="detail-card-body">
                  <div class="detail-row">
                    <span class="detail-label">Payment Number</span>
                    <span class="detail-value">{{ payment.payment_number || 'N/A' }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Payment Date</span>
                    <span class="detail-value">{{ formatDate(payment.payment_date) }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Period Start</span>
                    <span class="detail-value">{{ formatDate(payment.start_date) }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Period End</span>
                    <span class="detail-value">{{ formatDate(payment.end_date) }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Status</span>
                    <span class="detail-value">
                      <badge :type="getStatusBadge(payment.current_status)">
                        {{ payment.current_status || 'Draft' }}
                      </badge>
                    </span>
                  </div>
                </div>
              </div>

              <div class="detail-card">
                <div class="detail-card-header">
                  <md-icon>person</md-icon>
                  <h3>Labourer Information</h3>
                </div>
                <div class="detail-card-body">
                  <div class="detail-row">
                    <span class="detail-label">Name</span>
                    <span class="detail-value">
                      <clickable-link
                          v-if="payment.labourer"
                          :id="payment.labourer.id"
                          resource="Labourer"
                          :text="getLabourerName()"
                      />
                      <span v-else>N/A</span>
                    </span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Employee Code</span>
                    <span class="detail-value">{{ getLabourerCode() }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Phone</span>
                    <span class="detail-value">{{ getLabourerPhone() }}</span>
                  </div>
                </div>
              </div>

              <div class="detail-card">
                <div class="detail-card-header">
                  <md-icon>bar_chart</md-icon>
                  <h3>Totals</h3>
                </div>
                <div class="detail-card-body">
                  <div class="stat-row">
                    <div class="stat-item">
                      <span class="stat-value">{{ payment.total_items || 0 }}</span>
                      <span class="stat-label">Items</span>
                    </div>
                    <div class="stat-item">
                      <span class="stat-value">{{ payment.total_hours || 0 }}</span>
                      <span class="stat-label">Hours</span>
                    </div>
                  </div>
                  <div class="stat-row">
                    <div class="stat-item full-width">
                      <span class="stat-value success">{{ formatCurrency(payment.total_amount) }}</span>
                      <span class="stat-label">Total Amount</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="detail-card">
                <div class="detail-card-header">
                  <md-icon>history</md-icon>
                  <h3>Timeline</h3>
                </div>
                <div class="detail-card-body">
                  <div class="timeline-row">
                    <span class="timeline-label">Created</span>
                    <span class="timeline-value">{{ formatDateTime(payment.created_at) }}</span>
                  </div>
                  <div class="timeline-row" v-if="payment.approved_at">
                    <span class="timeline-label">Approved</span>
                    <span class="timeline-value">{{ formatDateTime(payment.approved_at) }}</span>
                  </div>
                  <div class="timeline-row" v-if="payment.paid_at">
                    <span class="timeline-label">Paid</span>
                    <span class="timeline-value">{{ formatDateTime(payment.paid_at) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Notes -->
            <div class="notes-card" v-if="payment.notes">
              <div class="detail-card-header">
                <md-icon>notes</md-icon>
                <h3>Notes</h3>
              </div>
              <div class="detail-card-body">
                <p class="notes-text">{{ payment.notes }}</p>
              </div>
            </div>
          </div>
        </md-tab>

        <!-- Items Tab -->
        <md-tab id="tab-items" md-label="Items" md-icon="list_alt">
          <div class="tab-content">
            {{paymentItems.length}}
            <table-data
                title="Payment Items"
                :columns="itemColumns"
                :data="paymentItems"
                :total="paymentItems.length"
                :searchable="true"
                search-placeholder="Search items..."
                :has-actions="false"
            >
              <template #cell-takeon_number="{ item }">
                <span class="detail-value">
                      <clickable-link
                          v-if="item.labourerSchedule"
                          :id="item.labourerSchedule.id"
                          resource="Labourer Schedule"
                          :text="item.labourerSchedule.takeon_number"
                      />
                      <span v-else>N/A</span>
                </span>
              </template>
              <template #cell-shift_date="{ item }">
                {{ formatDate(item.labourerSchedule?item.labourerSchedule.schedule.shift_date:'N/A') }}
              </template>

              <template #cell-hours_worked="{ item }">
                {{ item.hours_worked || 0 }} hrs
              </template>

              <template #cell-rate_per_hour="{ item }">
                {{ formatCurrency(item.rate_per_hour) }}
              </template>

              <template #cell-amount="{ item }">
                <strong class="amount">{{ formatCurrency(item.amount) }}</strong>
              </template>

              <template #cell-status="{ item }">
                <badge :type="getItemStatusBadge(item.status)">
                  {{ item.status || 'Pending' }}
                </badge>
              </template>
            </table-data>
          </div>
        </md-tab>
      </page-tabs>
    </template>
  </page-layout>
</template>

<script>
import PageLayout from '@/components/Layouts/PageLayout.vue';
import PageTabs from '@/components/Layouts/PageTabs.vue';
import TableData from '@/components/DataTable/TableData.vue';
import ClickableLink from '@/components/ClickableLink.vue';
import Badge from '@/components/Badge.vue';
import PageMixin from '@/mixins/page-mixin';
import Swal from 'sweetalert2';
import moment from 'moment';

export default {
  name: 'ViewLabourerPaymentPage',
  components: {
    PageLayout: PageLayout,
    PageTabs: PageTabs,
    TableData: TableData,
    ClickableLink: ClickableLink,
    Badge: Badge
  },
  mixins: [PageMixin],
  data: function () {
    return {
      payment: {
        id: null,
        payment_number: null,
        payment_date: null,
        start_date: null,
        end_date: null,
        total_amount: 0,
        total_hours: 0,
        total_items: 0,
        current_status: 'draft',
        notes: null,
        approved_at: null,
        paid_at: null,
        created_at: null,
        labourer: null
      },
      paymentItems: [],
      itemColumns: [
        { field: 'takeon_number', label: 'Takeon Number' },
        { field: 'shift_date', label: 'Date' },
        { field: 'shift_name', label: 'Shift' },
        { field: 'hours_worked', label: 'Hours' },
        { field: 'rate_per_hour', label: 'Rate' },
        { field: 'amount', label: 'Amount', align: 'text-right' },
        { field: 'status', label: 'Status' }
      ]
    };
  },
  computed: {
    pageTitle: function () {
      return this.payment.payment_number || 'Payment Details';
    },
    pageBreadcrumb: function () {
      return [
        { label: 'Labourer Payments', path: '/labourer-payments' },
        { label: this.payment.payment_number || 'Details' }
      ];
    },
    pageStatus: function () {
      return {
        label: this.payment.current_status || 'Draft',
        type: this.getStatusBadge(this.payment.current_status)
      };
    },
    pageCode: function () {
      return this.payment.uuid ? 'UUID: ' + this.payment.uuid : null;
    },
    pageInfoCards: function () {
      return [
        {
          icon: 'list_alt',
          label: 'Items',
          value: this.payment.total_items || 0,
          color: 'primary'
        },
        {
          icon: 'schedule',
          label: 'Total Hours',
          value: (this.payment.total_hours || 0) + ' hrs',
          color: 'info'
        },
        {
          icon: 'attach_money',
          label: 'Total Amount',
          value: this.formatCurrency(this.payment.total_amount),
          color: 'success'
        },
        {
          icon: 'check_circle',
          label: 'Status',
          value: this.payment.current_status || 'Draft',
          color: this.payment.current_status === 'paid' ? 'success' : 'warning'
        }
      ];
    }
  },
  created: function () {
    this.get();
  },
  methods: {
    async get() {
      try {
        var id = this.$route.params.id;
        await this.$store.dispatch('labourerPayments/view', {
          id: id,
          include: 'items.labourerSchedule.schedule,labourer'
        });
        this.payment = this.$store.getters['labourerPayments/labourerPayment'] || this.payment;
        this.paymentItems = this.payment.items || [];
      } catch (error) {
        console.error('Error fetching payment:', error);
        this.notifyException(error)
      }
    },

    goToEdit: function (id) {
      this.$router.push({ name: 'Edit Labourer Payment', params: { id: id } });
    },

    goBack: function () {
      this.$router.push({ name: 'List Labourer Payment' });
    },

    async approvePayment() {
      var result = await Swal.fire({
        title: 'Approve Payment?',
        text: 'Are you sure you want to approve this payment?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#4caf50',
        confirmButtonText: 'Yes, approve!'
      });

      if (!result.value) return;

      try {
        await this.$store.dispatch('labourerPayments/approvePaymentAction', this.payment.id);
        await Swal.fire({
          title: 'Approved!',
          text: 'Payment approved successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
        this.get();
      } catch (error) {
        this.$store.dispatch('alerts/error', 'Failed to approve payment');
      }
    },

    async markAsPaid() {
      var result = await Swal.fire({
        title: 'Mark as Paid?',
        text: 'Confirm that this payment has been paid.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#2196f3',
        confirmButtonText: 'Yes, mark as paid!'
      });

      if (!result.value) return;

      try {
        await this.$store.dispatch('labourerPayments/markAsPaidAction', this.payment.id);
        await Swal.fire({
          title: 'Paid!',
          text: 'Payment marked as paid.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
        this.get();
      } catch (error) {
        this.$store.dispatch('alerts/error', 'Failed to mark payment as paid');
      }
    },

    async cancelPayment() {
      var result = await Swal.fire({
        title: 'Cancel Payment?',
        input: 'textarea',
        inputLabel: 'Reason for cancellation',
        inputPlaceholder: 'Enter reason...',
        showCancelButton: true,
        confirmButtonColor: '#f44336',
        confirmButtonText: 'Cancel Payment',
        cancelButtonText: 'Keep'
      });

      if (!result.value) return;

      try {
        await this.$store.dispatch('labourerPayments/cancelPaymentAction', {
          id: this.payment.id,
          data: { reason: result.value }
        });
        await Swal.fire({
          title: 'Cancelled',
          text: 'Payment has been cancelled.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
        this.get();
      } catch (error) {
        this.$store.dispatch('alerts/error', 'Failed to cancel payment');
      }
    },

    getLabourerName: function () {
      if (this.payment.labourer) {
        return this.payment.labourer.full_name || this.payment.labourer.name || 'Unknown';
      }
      return 'N/A';
    },

    getLabourerCode: function () {
      if (this.payment.labourer) {
        return this.payment.labourer.emp_code || this.payment.labourer.labourer_number || 'N/A';
      }
      return 'N/A';
    },

    getLabourerPhone: function () {
      if (this.payment.labourer) {
        return this.payment.labourer.phone_number || 'N/A';
      }
      return 'N/A';
    },

    getStatusBadge: function (status) {
      var badges = {
        'draft': 'warning',
        'approved': 'info',
        'paid': 'success',
        'cancelled': 'danger'
      };
      return badges[status] || 'default';
    },

    getItemStatusBadge: function (status) {
      var badges = {
        'pending': 'warning',
        'approved': 'info',
        'paid': 'success'
      };
      return badges[status] || 'default';
    },

    formatDate: function (date) {
      if (!date) return 'N/A';
      return moment(date).format('DD MMM YYYY');
    },

    formatDateTime: function (date) {
      if (!date) return 'N/A';
      return moment(date).format('DD MMM YYYY, HH:mm');
    },

    formatCurrency: function (value) {
      if (!value) return 'TZS 0';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'TZS',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    }
  }
};
</script>

<style lang="scss" scoped>
.tab-content {
  padding: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

/* ============ PAYMENT HEADER CARD ============ */
.payment-header-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 30px;
  background: linear-gradient(135deg, $brand-primary 0%, $brand-primary-dark 100%);
  border-radius: 12px;
  color: white;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba($brand-primary, 0.25);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.payment-header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.payment-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .md-icon {
    color: white;
    font-size: 30px !important;
    width: 30px !important;
    height: 30px !important;
  }
}

.payment-header-info {
  .payment-number {
    margin: 0;
    font-size: 22px;
    font-weight: 700;
  }

  .payment-labourer {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 6px 0 0;
    font-size: 14px;
    opacity: 0.95;

    .md-icon {
      font-size: 16px !important;
      width: 16px !important;
      height: 16px !important;
    }
  }
}

.amount-display {
  text-align: right;
}

.amount-label {
  display: block;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.9;
}

.amount-value {
  display: block;
  font-size: 26px;
  font-weight: 700;
  margin-top: 4px;
}

/* ============ DETAILS GRID ============ */
.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.detail-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.detail-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: 2px solid $border-color;
  background: #fafbfc;

  .md-icon {
    color: $brand-primary;
    font-size: 22px !important;
    width: 22px !important;
    height: 22px !important;
  }

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: $text-primary;
  }
}

.detail-card-body {
  padding: 16px 20px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }
}

.detail-label {
  font-size: 13px;
  color: $text-muted;
  font-weight: 500;
}

.detail-value {
  font-size: 13px;
  color: $text-primary;
  font-weight: 600;
  text-align: right;
}

/* ============ STATS ============ */
.stat-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
}

.stat-item {
  text-align: center;
  padding: 12px 8px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid $brand-primary;

  &.full-width {
    grid-column: 1 / -1;
  }
}

.stat-value {
  display: block;
  font-size: 20px;
  font-weight: 700;
  color: $text-primary;
  margin-bottom: 4px;

  &.success {
    color: $brand-success;
  }
}

.stat-label {
  display: block;
  font-size: 11px;
  color: $text-muted;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

/* ============ TIMELINE ============ */
.timeline-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f5f5f5;
  font-size: 13px;

  &:last-child {
    border-bottom: none;
  }
}

.timeline-label {
  color: $text-muted;
  font-weight: 500;
}

.timeline-value {
  color: $text-primary;
  font-weight: 600;
}

/* ============ NOTES ============ */
.notes-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.notes-text {
  font-size: 14px;
  line-height: 1.6;
  color: $text-secondary;
  margin: 0;
}

.amount {
  color: $brand-success;
}

/* ============ DATA TABLE FULL WIDTH ============ */
::v-deep .data-table {
  width: 100%;
  max-width: 100%;

  .table-wrapper {
    width: 100%;
    overflow-x: auto;

    .md-table {
      width: 100%;
      table-layout: auto;
    }
  }
}

/* ============ RESPONSIVE ============ */
@media (max-width: 768px) {
  .payment-header-card {
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }

  .payment-header-left {
    flex-direction: column;
  }

  .amount-display {
    text-align: center;
  }

  .details-grid {
    grid-template-columns: 1fr;
  }
}
</style>